import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  ListSubheader,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Router as RouterIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  AccountCircle,
  ExitToApp,
  HowToReg as ApprovalIcon,
  CloudDownload as InstallIcon,
  CreditCard as SubscriptionsIcon,
  CreditCard,
  Assignment as RegistrationIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  ArrowDropDown as ArrowDropDownIcon,
  MenuBook as RunbookIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const NAV_GROUPS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: DashboardIcon,
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: DashboardIcon, adminOnly: true },
    ],
  },
  {
    id: 'gateways',
    label: 'Gateways',
    icon: RouterIcon,
    items: [
      { label: 'Gateways', path: '/gateways', icon: RouterIcon, adminOnly: false },
      { label: 'Approvals', path: '/gateway-approvals', icon: ApprovalIcon, adminOnly: true },
      { label: 'Registrations', path: '/registration-requests', icon: RegistrationIcon, adminOnly: true },
      { label: 'Install Gateway', path: '/install-gateway-with-nav', icon: InstallIcon, adminOnly: false, dividerBefore: true },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: CreditCard,
    items: [
      { label: 'My Subscription', path: '/my-subscription', icon: CreditCard, adminOnly: false },
      { label: 'Subscriptions', path: '/subscriptions', icon: SubscriptionsIcon, adminOnly: true },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: AdminIcon,
    items: [
      { label: 'Users', path: '/users', icon: PeopleIcon, adminOnly: true },
      { label: 'Shipping & rollout', path: '/admin/shipping', icon: RunbookIcon, adminOnly: true },
      { label: 'Email Settings', path: '/auto-approval-settings', icon: EmailIcon, adminOnly: true },
      { label: 'Settings', path: '/settings', icon: SettingsIcon, adminOnly: false },
    ],
  },
];

const navButtonSx = (active) => ({
  backgroundColor: active ? 'rgba(255,255,255,0.16)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
});

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [openGroupId, setOpenGroupId] = React.useState(null);

  const isAdmin = user?.isGatewayOwner === true;

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.adminOnly || isAdmin),
  })).filter((group) => group.items.length > 0);

  const isItemActive = (path) => location.pathname === path;
  const isGroupActive = (group) => group.items.some((item) => isItemActive(item.path));

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleGroupOpen = (event, groupId) => {
    setMenuAnchor(event.currentTarget);
    setOpenGroupId(groupId);
  };

  const handleGroupClose = () => {
    setMenuAnchor(null);
    setOpenGroupId(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleGroupClose();
    handleMobileMenuClose();
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    handleGroupClose();
    handleMobileMenuClose();
    navigate('/');
  };

  const renderDesktopNav = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {visibleGroups.map((group) => {
        const active = isGroupActive(group);

        if (group.items.length === 1) {
          const item = group.items[0];
          const Icon = item.icon;
          return (
            <Button
              key={group.id}
              color="inherit"
              startIcon={<Icon />}
              onClick={() => handleNavigation(item.path)}
              sx={navButtonSx(active)}
            >
              {item.label}
            </Button>
          );
        }

        const GroupIcon = group.icon;
        return (
          <React.Fragment key={group.id}>
            <Button
              color="inherit"
              startIcon={<GroupIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(event) => handleGroupOpen(event, group.id)}
              aria-haspopup="true"
              aria-expanded={openGroupId === group.id}
              aria-controls={openGroupId === group.id ? `${group.id}-menu` : undefined}
              sx={navButtonSx(active || openGroupId === group.id)}
            >
              {group.label}
            </Button>
            <Menu
              id={`${group.id}-menu`}
              anchorEl={menuAnchor}
              open={openGroupId === group.id}
              onClose={handleGroupClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { minWidth: 240, mt: 0.5 } }}
            >
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <React.Fragment key={item.path}>
                    {item.dividerBefore && <Divider />}
                    <MenuItem
                      selected={isItemActive(item.path)}
                      onClick={() => handleNavigation(item.path)}
                    >
                      <ListItemIcon>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{item.label}</ListItemText>
                    </MenuItem>
                  </React.Fragment>
                );
              })}
            </Menu>
          </React.Fragment>
        );
      })}
    </Box>
  );

  const renderMobileNav = () => (
    <List sx={{ flex: 1 }}>
      {visibleGroups.map((group) => (
        <React.Fragment key={group.id}>
          {group.items.length > 1 && (
            <ListSubheader
              sx={{
                bgcolor: 'transparent',
                lineHeight: '36px',
                fontWeight: 700,
                letterSpacing: 0.4,
              }}
            >
              {group.label}
            </ListSubheader>
          )}
          {group.items.map((item) => {
            const Icon = item.icon;
            return (
              <ListItemButton
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                selected={isItemActive(item.path)}
                sx={{
                  pl: group.items.length > 1 ? 3 : 2,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.12)',
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            );
          })}
        </React.Fragment>
      ))}
    </List>
  );

  return (
    <>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open menu"
              edge="start"
              onClick={handleMobileMenuToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box
              component="img"
              src="/eyesense-logo-v2.png"
              alt="EyeSense.ai Logo"
              onClick={() => navigate('/dashboard')}
              sx={{
                width: isMobile ? 46 : 58,
                height: isMobile ? 46 : 58,
                objectFit: 'contain',
                mr: 1.2,
                cursor: 'pointer',
              }}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontSize: isMobile ? '1rem' : '1.25rem',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/dashboard')}
            >
              EyeSense.ai Hub
            </Typography>
          </Box>

          {!isMobile && renderDesktopNav()}

          <IconButton
            size="large"
            edge="end"
            aria-label="account"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuClick}
            color="inherit"
            sx={{ ml: isMobile ? 0 : 2 }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {user?.username?.charAt(0).toUpperCase() || <AccountCircle />}
            </Avatar>
          </IconButton>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
              <Typography variant="body2" fontWeight="bold">
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.username}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {user?.email}
              </Typography>
              {isAdmin && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      display: 'inline-block',
                      fontWeight: 'bold',
                    }}
                  >
                    ADMIN
                  </Typography>
                </Box>
              )}
            </Box>

            <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
              <AccountCircle sx={{ mr: 1 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: 'primary.main',
          color: 'white',
        }}
        >
          <Typography variant="h6">
            Menu
          </Typography>
          <IconButton
            onClick={handleMobileMenuClose}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, py: 2, bgcolor: 'rgba(0,0,0,0.03)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
              {user?.username?.charAt(0).toUpperCase() || <AccountCircle />}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" fontWeight="bold" noWrap>
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.username}
              </Typography>
              <Typography variant="caption" color="textSecondary" noWrap>
                {user?.email}
              </Typography>
              {isAdmin && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      display: 'inline-block',
                      fontWeight: 'bold',
                    }}
                  >
                    ADMIN
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Divider />

        {renderMobileNav()}

        <Divider />

        <List>
          <ListItemButton onClick={() => handleNavigation('/profile')}>
            <ListItemIcon>
              <AccountCircle />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </ListItemButton>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon sx={{ color: 'error.main' }}>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ color: 'error.main' }} />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
};

export default NavBar;
